import type { IBaseObjStaticConstructor } from '@/objects/IBaseObjStaticConstructor';
import type { ISerialize } from '@/objects/ISerialize';
import { getPreferredLanguage } from '@/utilities';
import { marked } from 'marked';

export abstract class StaticBaseDao {
    private static root: string = '/api';

    /**
     * Run a DELETE request to the server.
     * @param url
     * @returns
     */
    protected static async Delete(url: string) {
        const resp = await fetch(this.root + '/' + url, {
            method: 'DELETE',
        });

        try {
            if (resp.ok) {
                return true;
            }
        } catch (ex) {
            throw new Error('general.unknownerror', { cause: new Error((ex as any).toString()) });
        }
        const content = await resp.text();

        throw this.createError(content, resp.status);
    }

    /**
     * Run a GET request to the server.
     * @param url
     * @returns
     */
    protected static async Get<T>(url: string) {
        const resp = await fetch(this.root + '/' + url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Locale': getPreferredLanguage().Locale,
            },
        });

        try {
            if (resp.ok) {
                return resp.json() as T;
            }
        } catch (ex) {
            throw new Error('general.unknownerror', { cause: new Error((ex as any).toString()) });
        }
        const content = await resp.text();

        throw this.createError(content, resp.status);
    }

    /**
     * Get a file octet stream and download it
     * @param url
     * @returns
     */
    protected static async GetRaw<T>(url: string) {
        const resp = await fetch(this.root + '/' + url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/octet-stream',
                'X-User-Locale': getPreferredLanguage().Locale,
            },
        });

        // Return the file so the browser downloads it
        return {
            stream: resp.body,
            contentType: resp.headers.get('Content-Type'),
            filename: resp.headers.get('Content-Disposition')?.split('filename=')[1]?.split(';')[0],
        };
    }

    /**
     * Run a POST request to the server.
     * @param url
     * @param data
     * @returns
     */
    protected static async Post<T>(url: string, data: ISerialize | Array<ISerialize> | null = null) {
        const body = data != null ? (Array.isArray(data) ? `[${data.map((x) => x.serialize())}]` : data.serialize()) : null;

        const resp = await fetch(this.root + '/' + url, {
            method: 'POST',
            body: body,
            headers: {
                'Content-Type': 'application/json',
                'X-User-Locale': getPreferredLanguage().Locale,
            },
        });

        try {
            if (resp.ok) {
                return resp.json() as T;
            }
        } catch (ex) {
            throw new Error('general.unknownerror', { cause: new Error((ex as any).toString()) });
        }
        const content = await resp.text();
        throw new Error(content);
    }

    /**
     * Run a POST request to the server, returning the stream of raw data and ContentType
     * @param url
     * @param data
     * @returns
     */
    protected static async PostRaw(
        url: string,
        data: ISerialize | Array<ISerialize> | null = null,
    ): Promise<{ stream: ReadableStream<Uint8Array> | null; contentType: string | null; filename?: string }> {
        const body = data != null ? (Array.isArray(data) ? `[${data.map((x) => x.serialize())}]` : data.serialize()) : null;

        const resp = await fetch(this.root + '/' + url, {
            method: 'POST',
            body: body,
            headers: {
                'Content-Type': 'application/json',
                'X-User-Locale': getPreferredLanguage().Locale,
            },
        });

        try {
            if (resp.ok) {
                return {
                    stream: resp.body,
                    contentType: resp.headers.get('Content-Type'),
                    filename: resp.headers.get('content-disposition')?.split('filename=')[1]?.split(';')[0],
                };
            }
        } catch (ex) {
            throw new Error('general.unknownerror', { cause: new Error((ex as any).toString()) });
        }
        const content = await resp.text();
        throw new Error(content);
    }

    /**
     * Run a request to the server to get a single object. this has to be a ISerialize object.
     * @param url
     * @param data
     * @returns
     */
    protected static async BaseFetch<T extends ISerialize>(
        type: IBaseObjStaticConstructor<T>,
        url: string,
        method: string = 'GET',
        data: ISerialize | Array<ISerialize> | null = null,
    ) {
        const body =
            method == 'POST' && data != null
                ? Array.isArray(data)
                    ? `[${data.map((x) => x.serialize())}]`
                    : data.serialize()
                : method == 'POST'
                  ? 'null'
                  : null;

        const resp = await fetch(this.root + '/' + url, {
            method: method,
            body: body,
            headers: {
                'Content-Type': 'application/json',
                'X-User-Locale': getPreferredLanguage().Locale,
            },
        });
        try {
            if (resp.ok) {
                const json = await resp.json();
                if (json == null) {
                    return null;
                }
                return type._FromServerJson(json);
            }
        } catch (ex) {
            try {
                throw new Error('general.unknownerror', { cause: new Error((ex as any).toString()) });
            } catch (e) {
                console.error(e);
                throw e;
            }
        }
        const content = await resp.text();

        throw this.createError(content, resp.status);
    }

    /**
     * Helper method to throw an error with the correct translation key.
     * @param content
     * @param httpstatus
     */
    private static createError(content: string, httpstatus: number): Error {
        // We know that from the backend we get a exception with the translation key in the message.
        // format: databaseexception.deleteuniqueconstraintviolation (StatusCode: 409)
        // We use regexp to get the translation key and the status code.
        const match = content.match(/(.*)\(StatusCode: \d+\)/gi);
        if (match) {
            let translationKey = match[0].replace(/\(StatusCode: \d+\)/gi, '').trim();
            const cause =
                httpstatus == 409 || httpstatus == 403 || httpstatus == 401 ? httpstatus.toString() : content.replace(match[0], '').trim();

            // Should we just reload the page?
            if (httpstatus == 401 && translationKey == 'session.notloggedin') {
                window.location.reload();
            } else if (httpstatus == 409) {
                // We need to check if the this is html or not. We check if it contains a < and >
                if (translationKey.includes('<') && translationKey.includes('>')) {
                    translationKey = marked.parse(translationKey).toString(); // We run the markdown parser on the translation key
                }
            }

            // TODO: Here we should handle the "Could not find event with uid" error, and redirect to the event list page.

            return new Error(translationKey, { cause: cause });
        }

        return new Error(content, { cause: httpstatus.toString() });
    }

    protected static async BaseFetchArray<T extends ISerialize>(
        type: IBaseObjStaticConstructor<T>,
        url: string,
        method: string = 'GET',
        data: ISerialize | Array<ISerialize> | null = null,
    ) {
        const body =
            method == 'POST' && data != null
                ? Array.isArray(data)
                    ? `[${data.map((x) => x.serialize())}]`
                    : data.serialize()
                : method == 'POST'
                  ? 'null'
                  : null;
        const resp = await fetch(this.root + '/' + url, {
            method: method,
            body: body,
            headers: {
                'Content-Type': 'application/json',
                'X-User-Locale': getPreferredLanguage().Locale,
            },
        });

        const arr = new Array<T>();
        try {
            if (resp.ok) {
                const json = await resp.json();
                if (json == null) {
                    return arr;
                }

                json.forEach((j: any) => {
                    arr.push(type._FromServerJson(j));
                });

                return arr;
            }
        } catch (ex) {
            try {
                throw new Error('general.unknownerror', { cause: new Error((ex as any).toString()) });
            } catch (e) {
                console.error(e);
                throw e;
            }
        }
        const content = await resp.text();

        throw this.createError(content, resp.status);
    }

    /**
     * This will post a formData object to the server and return the result as a T.
     * @param url
     * @param data
     * @returns T
     */
    protected static async PostFormData<T extends ISerialize>(type: IBaseObjStaticConstructor<T>, url: string, data: FormData) {
        const resp = await fetch(this.root + '/' + url, {
            method: 'POST',
            body: data,
        });

        const arr = new Array<T>();
        try {
            if (resp.ok) {
                const json = await resp.json();
                if (json == null) {
                    return arr;
                }

                json.forEach((j: any) => {
                    arr.push(type._FromServerJson(j));
                });

                return arr;
            }
        } catch (ex) {
            try {
                throw new Error('general.unknownerror', { cause: new Error((ex as any).toString()) });
            } catch (e) {
                console.error(e);
                throw e;
            }
        }
        const content = await resp.text();

        throw this.createError(content, resp.status);
    }

    // TODO: Ikke fan av at jeg har med String i navnet.. men må tror jeg, for jeg kan ikke definere nøkkelen bedre
    protected static async FetchMapString<T extends ISerialize>(type: IBaseObjStaticConstructor<T>, url: string) {
        const resp = await fetch(this.root + '/' + url);

        try {
            if (resp.ok) {
                const json = await resp.json();
                const data = new Map<string, T>();

                const keys = Object.keys(json);
                keys.forEach((k) => {
                    const obj = type._FromServerJson(json[k]);
                    data.set(k, obj);
                });

                return data;
            }
        } catch (ex) {
            throw new Error('general.unknownerror', { cause: new Error((ex as any).toString()) });
        }
        const content = await resp.text();

        throw this.createError(content, resp.status);
    }
    /**
     * Makes getstring that will fit a [FromQuery] type[]
     * @param list
     * @param name
     * @returns
     */
    protected static makeGetStringFromList(list: Array<any> | undefined | null, name: string) {
        if (!list) {
            return '';
        }
        return `${list.length > 0 ? `&${name}=` : ''}${list.join(`&${name}=`)}`;
    }
}
